/**
 * Map plan-preview JSON onto the Plan step's view model.
 * Agronomy stays on the backend; this file only formats and paints.
 */

const PAPER = '#f0eada';
const KHAKI = '#e8e1cb';
const LODEN = '#3b4e3a';
const CREAM = '#efe7d2';
const INK = '#221e18';
const GOLD = '#c08a20';
const RUST = '#b4501c';
const SOIL = '#8b5e3c';

export const PLAN_TONE_COLORS = Object.freeze({
  rust: { bg: RUST, fg: CREAM },
  forest: { bg: LODEN, fg: CREAM },
  khaki: { bg: KHAKI, fg: INK },
  gold: { bg: GOLD, fg: INK },
  soil: { bg: SOIL, fg: CREAM },
  paper: { bg: PAPER, fg: INK },
});

export function planToneColors(tone) {
  return PLAN_TONE_COLORS[tone] || PLAN_TONE_COLORS.khaki;
}

export const CATALOG_BLEND_KEYS = Object.freeze(['fall', 'rye', 'soil', 'spring', 'perennial', 'turkey']);

const CATALOG_BLEND_NAMES = Object.freeze({
  fall: 'Fall Draw Blend',
  rye: 'Winter Rye Blend',
  soil: 'Soil Builder Rotation',
  spring: 'Summer Hold Blend',
  perennial: 'Perennial Clover Base',
  turkey: 'Brood Range Mix',
});

export function sanitizeBlendKey(raw) {
  const value = String(raw || '').trim().toLowerCase();
  return CATALOG_BLEND_KEYS.includes(value) ? value : '';
}

export function catalogBlendDisplayName(key) {
  const catalogKey = sanitizeBlendKey(key);
  return catalogKey ? (CATALOG_BLEND_NAMES[catalogKey] || '') : '';
}

/** Clock/kicker fallback when preview has not loaded yet. Not a blend pick. */
export function blendKeyFromAnswers(answers = {}, preferKey = '') {
  const picked = sanitizeBlendKey(preferKey);
  if (picked) return picked;
  const season = String(answers?.season || '').trim();
  const goal = String(answers?.goal || '').trim();
  if (season === 'spring') return goal === 'turkey' ? 'turkey' : 'spring';
  if (season === 'year') return 'perennial';
  if (goal === 'turkey') return 'turkey';
  if (goal === 'soil') return 'soil';
  if (goal === 'hold') return 'perennial';
  return 'fall';
}

export function planPreviewRequest({ answers, place, acres, blend, draftSoilPh } = {}) {
  return {
    acres: Number(acres) || 0,
    wizardAnswers: answers || {},
    place: planPreviewPlaceFromGround(place, draftSoilPh),
    blend: sanitizeBlendKey(blend),
  };
}

function asNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** AreaGroup field names the backend `normalize_place` already understands. */
export function planPreviewPlaceFromGround(place, draftSoilPh = '') {
  const p = place && typeof place === 'object' ? place : {};
  const override = asNumber(draftSoilPh);
  const ph = override != null
    ? override
    : asNumber(p.soil_ph != null ? p.soil_ph : p.ph);
  return {
    usda_zone_code: String(p.usda_zone_code || p.zone || '').trim(),
    soil_series: String(p.soil_series || p.series || '').trim(),
    soil_type_id: String(p.soil_type_id || p.soilTypeId || p.soil_type || '').trim(),
    soil_drainage: String(p.soil_drainage || p.drain || '').trim(),
    soil_ph: ph,
    first_frost_label: String(p.first_frost_label || p.first || '').trim(),
    last_frost_label: String(p.last_frost_label || p.last || '').trim(),
  };
}

function cartRateLabel(row) {
  const rate = asNumber(row?.rate) ?? 0;
  if (row?.rate_unit === 'tons_per_acre') return `${rate} TONS / ACRE`;
  return `${Math.round(rate)} LBS / ACRE`;
}

function cartQtyLabel(row) {
  const qty = asNumber(row?.quantity) ?? 0;
  if (row?.quantity_unit === 'tons') return `${qty} tons`;
  return `${Math.round(qty)} lbs`;
}

function cartBagsLabel(row) {
  if (row?.bulk) return 'BULK SPREAD';
  const bags = Math.round(asNumber(row?.bags) ?? 0);
  const size = Math.round(asNumber(row?.bag_size_lbs) || 50);
  return `${bags} × ${size} LB BAG`;
}

function altFill(match) {
  if (match >= 75) return LODEN;
  if (match >= 60) return GOLD;
  return KHAKI;
}

/**
 * @returns {object|null} The shape PlantingPlanStep already renders.
 */
export function buildPlanViewModel(payload, { orderEnabled = false, zone = '' } = {}) {
  const blend = payload?.blend;
  if (!blend || typeof blend !== 'object' || !blend.name) return null;

  const matchValue = asNumber(payload?.match?.value) ?? 0;
  const acres = asNumber(payload?.acres) ?? 0;
  const acresLabel = acres > 0 ? (Math.round(acres * 10) / 10).toFixed(1) : '—';
  const tags = Array.isArray(blend.tags) ? blend.tags : [];
  const reasons = Array.isArray(payload?.reasons) ? payload.reasons : [];
  const cart = Array.isArray(payload?.cart) ? payload.cart : [];
  const alts = Array.isArray(payload?.alts) ? payload.alts : [];
  const costTotal = asNumber(payload?.cost?.total) ?? 0;
  const seedLbs = asNumber(payload?.seed_total_lbs) ?? 0;
  const fertLbs = asNumber(payload?.fertilizer?.total_lbs) ?? 0;
  const zoneLabel = String(zone || '').trim().toUpperCase() || '—';

  return {
    recName: blend.name,
    recBlurb: blend.blurb || '',
    recMatch: matchValue,
    recMatchPct: `${matchValue}%`,
    recMatchNote: payload?.match?.note || '',
    recTags: tags.map((tag) => {
      const paint = planToneColors(tag.tone);
      return {
        label: tag.label || '',
        bg: paint.bg,
        fg: paint.fg,
        tone: tag.tone || 'khaki',
      };
    }),
    acresLabel,
    zone: zoneLabel,
    reasons: reasons.map((reason, index) => {
      const paint = planToneColors(reason.tone);
      return {
        id: reason.id || reason.tag || `reason-${index}`,
        tag: reason.tag || '',
        title: reason.title || '',
        text: reason.text || '',
        chip: paint.bg,
        chipFg: paint.fg,
        alt: index % 2 === 1,
      };
    }),
    reasonCount: `${reasons.length} FACTOR${reasons.length === 1 ? '' : 'S'}`,
    cart: cart.map((row, index) => ({
      name: row.name,
      rate: cartRateLabel(row),
      qty: cartQtyLabel(row),
      bags: cartBagsLabel(row),
      alt: index % 2 === 1,
    })),
    totalLbs: seedLbs + fertLbs,
    costTotal: `$${Math.round(costTotal)}`,
    alts: alts.map((alt) => {
      const match = asNumber(alt.match) ?? 0;
      return {
        name: alt.name,
        match,
        pct: `${match}%`,
        note: alt.note || '',
        fill: altFill(match),
        key: alt.key || '',
        selectable: Boolean(alt.selectable),
      };
    }),
    orderNote: orderEnabled
      ? 'Send this list to a supplier near your town and have it waiting at the counter.'
      : 'Ordering from a supply store inside Yona is in the works. For now, take the list to your co-op or use Seed Stores in the toolkit to find one nearby.',
    orderLabel: orderEnabled ? 'ORDER FROM A STORE' : 'ORDER SEED · COMING SOON',
    orderDisabled: !orderEnabled,
  };
}

export function planPreviewZoneLabel(place) {
  const code = String(place?.usda_zone_code || place?.zone || '').trim().toUpperCase();
  return code || '—';
}
